// Frontend-side rate limiting for the "短信提醒" button in chat. The
// real cap will be enforced by the backend later; this is UX guidance
// so a user doesn't hammer the button.
//
// Limits per (currentUserId, threadId):
//   * 3 sends per local calendar day (Asia/Shanghai)

const STORAGE_PREFIX = 'sms_reminder:';
export const SMS_DAILY_LIMIT = 3;

interface QuotaRecord {
    dayKey: string;       // local Asia/Shanghai date "YYYY-MM-DD"
    count: number;        // sends so far today
    lastSentAt: number;   // epoch ms of the most recent send
}

// Today's calendar date in Asia/Shanghai (UTC+8) — independent of the
// browser's local timezone so the cap behaves the same for users
// abroad.
function shanghaiDayKey(now: number): string {
    const utcMs = now + 8 * 60 * 60 * 1000;
    const d = new Date(utcMs);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function key(userId: string, threadId: string): string {
    return `${STORAGE_PREFIX}${userId}:${threadId}`;
}

function read(userId: string, threadId: string): QuotaRecord | null {
    try {
        const raw = localStorage.getItem(key(userId, threadId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.dayKey !== 'string' || typeof parsed?.count !== 'number' || typeof parsed?.lastSentAt !== 'number') {
            return null;
        }
        return parsed as QuotaRecord;
    } catch {
        return null;
    }
}

function write(userId: string, threadId: string, rec: QuotaRecord): void {
    try {
        localStorage.setItem(key(userId, threadId), JSON.stringify(rec));
    } catch {
        // Storage full or disabled; silently ignore — frontend cap is
        // best-effort, server enforces the real limit.
    }
}

export interface QuotaStatus {
    canSend: boolean;
    remainingToday: number;          // 0..SMS_DAILY_LIMIT
    reason?: 'daily_limit';
}

export function getQuotaStatus(userId: string, threadId: string, now: number = Date.now()): QuotaStatus {
    const today = shanghaiDayKey(now);
    const rec = read(userId, threadId);
    const sameDay = rec?.dayKey === today;
    const count = sameDay ? rec!.count : 0;
    const remainingToday = Math.max(0, SMS_DAILY_LIMIT - count);

    if (remainingToday <= 0) {
        return { canSend: false, remainingToday: 0, reason: 'daily_limit' };
    }
    return { canSend: true, remainingToday };
}

// Records a successful send. Caller decides when to invoke (i.e. after
// the backend confirms the SMS was queued).
export function recordSend(userId: string, threadId: string, now: number = Date.now()): void {
    const today = shanghaiDayKey(now);
    const rec = read(userId, threadId);
    const sameDay = rec?.dayKey === today;
    const next: QuotaRecord = {
        dayKey: today,
        count: (sameDay ? rec!.count : 0) + 1,
        lastSentAt: now,
    };
    write(userId, threadId, next);
}
